/**
 * ITEM "SIN INVENTARIO" DE UNA FACTURA DE COMPRA (COMPRAS-BORRADOR-ANULACION-T1, contrato v0.2 de A #31032; API 1.6.811).
 *
 * Un flete o un servicio no va al inventario. Lo marca UNA PERSONA (PATCH del item con sinInventario) y desde ese
 * momento el item cuenta como resuelto, igual que uno casado a un insumo. La suposicion del OCR (es_inventariable
 * = false) NO lo resuelve: solo sugiere la casilla.
 *   resuelto = MATCH  |  sin_inventario_por lleno   (la misma regla que SqlItemResuelto en la API)
 * Marcar un item casado lo rechaza la API (409 ITEM_CASADO); aqui se evita antes de gastar el viaje.
 *
 * @module application/compras/sinInventario
 */

/**
 * El item esta casado. Basta MATCH, como en la API (SqlItemResuelto y la regla de ITEM_CASADO miran solo MATCH): si el
 * panel pidiera ademas insumo_id, un MATCH sin insumo contaria como pendiente y su casilla chocaria con un 409 (Q #31114).
 */
export function estaCasado(it) {
  return !!it && it.match_status === 'MATCH';
}

/** Una persona lo marco sin inventario. */
export function marcadoSinInventario(it) {
  return !!(it && it.sin_inventario_por);
}

/** Resuelto = casado a un insumo o marcado sin inventario por una persona. */
export function itemResuelto(it) {
  return estaCasado(it) || marcadoSinInventario(it);
}

/** El OCR cree que no va a inventario y nadie lo ha resuelto: se sugiere la casilla, no se marca sola. */
export function sugerirSinInventario(it) {
  return !!it && it.es_inventariable === false && !itemResuelto(it);
}

/** Cuantos items estan resueltos de cuantos. */
export function conteoResueltos(items) {
  const lista = Array.isArray(items) ? items : [];
  return { resueltos: lista.filter(itemResuelto).length, total: lista.length };
}

/**
 * El cambio para el PATCH del item. marcar=true pide sin inventario; false lo quita.
 * Devuelve { patch } o { error } sin llamar a la API cuando ya se sabe la respuesta.
 */
export function cambioSinInventario(it, marcar) {
  if (!it || !it.id) return { error: 'SIN_ITEM' };
  if (marcar && estaCasado(it)) return { error: 'ITEM_CASADO' };
  if (marcar === marcadoSinInventario(it)) return { error: 'SIN_CAMBIO' };
  return { patch: { sin_inventario: !!marcar } };
}

/** Texto para la persona segun el codigo que devuelva la API o la validacion local. */
export function textoErrorSinInventario(codigoOMensaje) {
  const s = String(codigoOMensaje || '');
  if (s.includes('ITEM_CASADO')) return 'Este item ya esta casado a un insumo: quitale el insumo antes de marcarlo sin inventario.';
  if (s.includes('SIN_CAMBIO')) return 'El item ya estaba asi.';
  if (s.includes('FORBIDDEN') || s.includes('403')) return 'No tienes permiso para cambiar este item.';
  return 'No pudimos guardar el cambio ahora. Intenta de nuevo en un momento.';
}

/**
 * El 409 de aprobar (nexo-api 578, D-139). ProblemDetails con extensions en la raiz:
 *   { detail: 'ITEMS_SIN_RESOLVER: ...', codigo: 'ITEMS_SIN_RESOLVER', items: [{ id, descripcion, matchStatus, esInventariableOcr }] }
 *   { detail: 'FACTURA_ANULADA: ...', codigo: 'FACTURA_ANULADA' }
 * Cualquier otro 409 es el idempotente del contrato #3362 (alguien la aprobo primero): codigo null.
 * El codigo se lee de details.codigo, de details.extensions.codigo o del prefijo del mensaje.
 */
export function rechazoDeAprobar(res) {
  const d = (res && res.details) || {};
  const ext = d.extensions || d.Extensions || {};
  const texto = String(d.detail || d.mensaje || d.message || (res && res.error) || '');
  let codigo = d.codigo || d.Codigo || ext.codigo || ext.Codigo || null;
  if (!codigo && texto.startsWith('ITEMS_SIN_RESOLVER')) codigo = 'ITEMS_SIN_RESOLVER';
  if (!codigo && texto.startsWith('FACTURA_ANULADA')) codigo = 'FACTURA_ANULADA';
  const crudos = Array.isArray(d.items) ? d.items : Array.isArray(ext.items) ? ext.items : [];
  const items = crudos.map((i) => ({
    id: String(i.id ?? i.Id ?? ''),
    descripcion: String(i.descripcion ?? i.Descripcion ?? ''),
    matchStatus: i.matchStatus ?? i.MatchStatus ?? 'PENDING',
    sugerirSinInventario: (i.esInventariableOcr ?? i.EsInventariableOcr) === false
  }));
  return { codigo, items };
}
